import { show } from "@ebay/nice-modal-react";
import type { AnimationItem } from "lottie-web";
import tippy, { type Instance, type Props } from "tippy.js";
import { canv } from "@/canv/global";
import { AiAnswer } from "@/elements/aiAnswer";
import { Group } from "@/group";
import { AiAgentFreeModal } from "@/interface/ui/modals/AiAgentFreeModal";
import { showSnackbar } from "@/snackbar/snackbar";
import type { widgetOption } from "../widgetMenu";
import { wmBase } from "../wmBase";
import {
    AI_MENU_BUTTON_ANIMATION_CLASS,
    AI_MENU_BUTTON_CLASS,
    AI_MENU_BUTTON_SVG_CLASS,
    AI_SUBMENU_BUTTON_CLASS,
    AI_SUBMENU_CLASS,
    AI_SUBMENU_HEADER_CLASS,
    AI_SUBMENU_ITEM_CLASS,
    AI_SUBMENU_TITLE_CLASS,
    CREATE_SUB_MENU_ITEMS,
    EDIT_SUB_MENU_ITEMS,
    SNACKBAR_TIMEOUT,
    TEXT_WIDGET_LEFT_MARGIN,
    TOAST_FREE_CONTENT,
    TOAST_PRO_CONTENT,
} from "./constants";
import {
    checkIsAiGeneratedTextInWidget,
    checkIsWidgetsQuantityExceeded,
    createButtonAnimation,
    processSelectedWidgets,
} from "./helpers";
import type { AiModes, CreateSubMenuActions, EditSubMenuActions } from "./types";

let animation: AnimationItem | null;
let tippyInstance: Instance<Props> | null;

export class wmAiAssistant extends wmBase {
    constructor(options: widgetOption<string>[]) {
        super();
        this.options = options;
    }

    build() {
        const isAiButtonDisabled = canv.intercom.disableAiEvents;

        return `
                <button type="button" class="${AI_MENU_BUTTON_CLASS}" ${isAiButtonDisabled ? "disabled" : ""}>
                    <svg class="${AI_MENU_BUTTON_SVG_CLASS}" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" ${isAiButtonDisabled ? `style="display: none"` : ""}>
                        <rect width="20" height="20" rx="10" fill="#343434" />
                        <rect width="20" height="20" rx="10" fill="url(#paint0_linear_350_1955)" />
                        <path
                            d="M9.63533 4.26711C9.74804 3.911 10.252 3.911 10.3647 4.26711L11.6553 8.34474L15.7329 9.63536C16.089 9.74807 16.089 10.252 15.7329 10.3647L11.6553 11.6553L10.3647 15.7329C10.252 16.0891 9.74804 16.0891 9.63533 15.7329L8.34471 11.6553L4.26708 10.3647C3.91097 10.252 3.91097 9.74807 4.26708 9.63536L8.34471 8.34474L9.63533 4.26711Z"
                            fill="white" />
                        <defs>
                            <linearGradient id="paint0_linear_350_1955" x1="-1.28555" y1="-1.15663" x2="25.3721" y2="6.13862"
                            gradientUnits="userSpaceOnUse">
                            <stop offset="0.01" stop-color="#5ED3FF" />
                            <stop offset="0.97" stop-color="#DE12FF" />
                            </linearGradient>
                        </defs>
                    </svg>
                    <div class="${AI_MENU_BUTTON_ANIMATION_CLASS}" ${isAiButtonDisabled ? `style="display: block"` : ""}></div>
                </button>
                <div class="wm_dividor"></div>
            `;
    }

    init() {
        if (animation) {
            animation.destroy();
            animation = null;
        }

        if (tippyInstance) {
            tippyInstance.destroy();
            tippyInstance = null;
        }

        const button = document.querySelector(`.${AI_MENU_BUTTON_CLASS}`);
        const animationWrapper: HTMLDivElement | null = document.querySelector(
            `.${AI_MENU_BUTTON_ANIMATION_CLASS}`,
        );

        if (button && animationWrapper) {
            const isProfessional: boolean = canv.getInitDataProp("isProfessional");
            const callback = isProfessional ? this.toggleSubMenu : this.toggleModal;

            button.removeEventListener("click", callback);
            button.addEventListener("click", callback);

            if (canv.intercom.disableAiEvents) {
                animation = createButtonAnimation(animationWrapper, canv.host);
            }

            tippyInstance = tippy(`.${AI_MENU_BUTTON_CLASS}`, {
                content: isProfessional ? TOAST_PRO_CONTENT : TOAST_FREE_CONTENT,
                placement: "bottom",
            })[0];
        }
    }

    addCreateMenuCategory(aiSubMenu: HTMLDivElement) {
        const subMenuTitle = document.createElement("div");
        subMenuTitle.classList.add(AI_SUBMENU_ITEM_CLASS, AI_SUBMENU_TITLE_CLASS);
        subMenuTitle.textContent = "Создать";
        aiSubMenu.appendChild(subMenuTitle);

        for (const item of CREATE_SUB_MENU_ITEMS) {
            const itemElement = document.createElement("button");
            itemElement.type = "button";
            itemElement.classList.add(AI_SUBMENU_ITEM_CLASS, AI_SUBMENU_BUTTON_CLASS);
            itemElement.setAttribute("data-mode", "create");
            itemElement.setAttribute("data-action", item.action);
            itemElement.innerHTML = `${item.icon} ${item.title}`;
            aiSubMenu.appendChild(itemElement);
        }
    }

    addEditMenuCategory(aiSubMenu: HTMLDivElement) {
        const subMenuTitle = document.createElement("div");
        subMenuTitle.classList.add(AI_SUBMENU_ITEM_CLASS, AI_SUBMENU_TITLE_CLASS);
        subMenuTitle.textContent = "Изменить тексты";
        aiSubMenu.appendChild(subMenuTitle);

        const selectedWidget = canv.canvasController.selected[0].onlyChild();
        let isAiGeneratedTextInWidget = false;
        if (selectedWidget) {
            isAiGeneratedTextInWidget = checkIsAiGeneratedTextInWidget(selectedWidget);
        }

        for (const item of EDIT_SUB_MENU_ITEMS) {
            if (isAiGeneratedTextInWidget && item.action === "fix_errors") {
                continue;
            }

            const itemElement = document.createElement("button");
            itemElement.type = "button";
            itemElement.classList.add(AI_SUBMENU_ITEM_CLASS, AI_SUBMENU_BUTTON_CLASS);
            itemElement.setAttribute("data-mode", "edit");
            itemElement.setAttribute("data-action", item.action);
            itemElement.innerHTML = `${item.icon} ${item.title}`;
            aiSubMenu.appendChild(itemElement);
        }
    }

    createAiAnswerWidget(action: CreateSubMenuActions) {
        const { startX, startY, width } = canv.canvasController;

        const xForTextWidget = startX + width + TEXT_WIDGET_LEFT_MARGIN;

        const newAiAnswerWidget = new AiAnswer(xForTextWidget, startY, action);
        const newGroup = new Group(true);

        newAiAnswerWidget.setParent(newGroup);
        canv.canvasController.addGroup(newGroup);

        canv.eventManager.openRecord();
        canv.eventManager.createBuildEvent("create", newGroup);
        canv.eventManager.closeRecord();

        return newAiAnswerWidget.UID;
    }

    handleMenuClick = (event: Event) => {
        const target = event.target as HTMLElement;
        const item = target.closest(`.${AI_SUBMENU_BUTTON_CLASS}`);

        if (!item) {
            return;
        }

        this.toggleSubMenu();

        const mode = item.getAttribute("data-mode") as AiModes;
        const action = item.getAttribute("data-action") as
            | CreateSubMenuActions
            | EditSubMenuActions;

        const isWidgetsQuantityExceeded = checkIsWidgetsQuantityExceeded(
            canv.canvasController.selected,
        );

        if (isWidgetsQuantityExceeded) {
            showSnackbar("Можно выбрать не более 70 элементов", { duration: SNACKBAR_TIMEOUT });

            return;
        }

        const { widgetUIDs } = processSelectedWidgets(canv.canvasController.selected, mode);

        if (!widgetUIDs.length) {
            showSnackbar("Недостаточно текста для анализа", { duration: SNACKBAR_TIMEOUT });

            return;
        }

        let targetWidgetUID: string | undefined;

        if (mode === "create") {
            targetWidgetUID = this.createAiAnswerWidget(action as CreateSubMenuActions);
        }

        //Для включения механизма undo/redo нужно получать стейт виджетов из processSelectedWidgets и передавать сюда
        canv.intercom.sendAiMessage(mode, action, widgetUIDs, targetWidgetUID);

        canv.intercom.disableAiEvents = true;
        const button: HTMLButtonElement | null = document.querySelector(`.${AI_MENU_BUTTON_CLASS}`);
        const buttonSvg: SVGSVGElement | null = document.querySelector(
            `.${AI_MENU_BUTTON_SVG_CLASS}`,
        );
        const animationWrapper: HTMLDivElement | null = document.querySelector(
            `.${AI_MENU_BUTTON_ANIMATION_CLASS}`,
        );

        if (button && buttonSvg && animationWrapper) {
            button.disabled = true;
            buttonSvg.style.display = "none";

            if (!animation) {
                animation = createButtonAnimation(animationWrapper, canv.host);
            } else {
                animationWrapper.style.display = "block";
            }
        }
    };

    toggleSubMenu = () => {
        const aiSubMenu = document.querySelector(`.${AI_SUBMENU_CLASS}`);

        if (aiSubMenu) {
            aiSubMenu.removeEventListener("click", this.handleMenuClick);
            aiSubMenu.outerHTML = "";
            return;
        }

        const otherSubMenu = document.querySelector(".widgetSubMenu");

        if (otherSubMenu) {
            otherSubMenu.outerHTML = "";
        }

        canv.widgetMenu.subMenuBoss = this.className;
        const newAiSubMenu = document.createElement("div");
        newAiSubMenu.classList.add("widgetSubMenu", AI_SUBMENU_CLASS);
        newAiSubMenu.style.opacity = "0";
        newAiSubMenu.addEventListener("click", this.handleMenuClick);
        document.body.prepend(newAiSubMenu);

        const subMenuHeader = document.createElement("div");
        subMenuHeader.classList.add(AI_SUBMENU_ITEM_CLASS, AI_SUBMENU_HEADER_CLASS);
        subMenuHeader.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="20" height="20" rx="10" fill="#343434" />
                <rect width="20" height="20" rx="10" fill="url(#paint0_linear_350_1955)" />
                <path
                    d="M9.63533 4.26711C9.74804 3.911 10.252 3.911 10.3647 4.26711L11.6553 8.34474L15.7329 9.63536C16.089 9.74807 16.089 10.252 15.7329 10.3647L11.6553 11.6553L10.3647 15.7329C10.252 16.0891 9.74804 16.0891 9.63533 15.7329L8.34471 11.6553L4.26708 10.3647C3.91097 10.252 3.91097 9.74807 4.26708 9.63536L8.34471 8.34474L9.63533 4.26711Z"
                    fill="white" />
                <defs>
                    <linearGradient id="paint0_linear_350_1955" x1="-1.28555" y1="-1.15663" x2="25.3721" y2="6.13862"
                    gradientUnits="userSpaceOnUse">
                    <stop offset="0.01" stop-color="#5ED3FF" />
                    <stop offset="0.97" stop-color="#DE12FF" />
                    </linearGradient>
                </defs>
            </svg> Ваш помощник
        `;
        newAiSubMenu.appendChild(subMenuHeader);

        this.addCreateMenuCategory(newAiSubMenu);

        if (canv.canvasController.selected.length === 1) {
            this.addEditMenuCategory(newAiSubMenu);
        }

        setTimeout(canv.widgetMenu.TryLocateMenu.bind(canv.widgetMenu), 50);
    };

    toggleModal = () => {
        show(AiAgentFreeModal);
    };

    name = "wm_aiAssistant";
    className = "wm_aiassistant";
}
